import { displayPatientCode } from './patientCode';

// ── UUID helpers ──────────────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const asUuidOrNull = (v) => {
  if (v == null || v === '') return null;
  const s = String(v);
  return UUID_RE.test(s) ? s : null;
};

// ── List-page constants ───────────────────────────────────────────────────────
export const VIEW_ORDER_LOCK_MS = 5 * 60 * 1000;

export const minsUntilViewable = (createdAt) => {
  if (!createdAt) return 0;
  const elapsed = Date.now() - new Date(createdAt).getTime();
  const remaining = VIEW_ORDER_LOCK_MS - elapsed;
  return remaining > 0 ? Math.ceil(remaining / 60_000) : 0;
};

export const STATUS_COLORS = {
  ORDERED:   'badge-pending',
  COLLECTED: 'badge-confirmed',
  COMPLETED: 'badge-active',
  CANCELLED: 'badge-inactive',
};

// ── Partner response parsers ──────────────────────────────────────────────────
export function parsePartnerCreateOrderResponse(data) {
  if (!data || typeof data !== 'object') return {};
  const nested = data.data && typeof data.data === 'object' ? data.data : null;
  const src = nested || data;
  return {
    orderId:    src.orderId    || src.order_id    || null,
    orderNo:    src.orderNo    || src.order_no    || null,
    status:     src.status     || null,
    message:    src.message    || null,
    refOrderNo: src.refOrderNo || src.ref_order_no || null,
    raw: data,
  };
}

export function buildLabTestStorageRecord({
  createOrderPayload,
  partnerResponse,
  razorpay,
  patient,
  sku,
  slot,
  appointmentDate,
}) {
  const partner = parsePartnerCreateOrderResponse(partnerResponse);
  const refOrderNo =
    partner.refOrderNo || createOrderPayload?.attributes?.refOrderNo || null;
  const price =
    sku?.rate?.sellingPrice ?? sku?.rate?.listingPrice ?? null;

  return {
    id: `lt_${Date.now()}`,
    testCode: refOrderNo,
    patientId: patient?.id ?? null,
    patientName: `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
    patientCode: displayPatientCode(patient?.patientCode),
    patientPhone: patient?.phone || '',
    patientEmail: patient?.email || '',
    plan: sku?.name || '',
    planId: sku?.id || '',
    planType: sku?.type || '',
    noOfTests: sku?.noOfTestsIncluded ?? sku?.testsIncluded?.length ?? null,
    testsIncluded: sku?.testsIncluded || [],
    price,
    currency: sku?.rate?.currency || 'INR',
    fastingRequired: sku?.flags?.isFastingRequired ? 'Yes' : 'No',
    priority: 'Routine',
    status: 'ORDERED',
    appointmentDate: appointmentDate || createOrderPayload?.appointment?.date || '',
    appointmentTime:
      createOrderPayload?.appointment?.startTime
      || slot?.startTime
      || slot?.slotTime
      || slot?.time
      || '',
    appointmentEndTime: slot?.endTime || slot?.toTime || '',
    appointmentTimeZone: createOrderPayload?.appointment?.timeZone || 'Asia/Kolkata',
    collectionAddress: createOrderPayload?.address || null,
    partnerOrderId: partner.orderId,
    partnerOrderNo: partner.orderNo,
    partnerOrderStatus: partner.status,
    partnerOrderMessage: partner.message,
    razorpayPaymentId: razorpay?.razorpay_payment_id || null,
    razorpayOrderId: razorpay?.razorpay_order_id || null,
    razorpaySignature: razorpay?.razorpay_signature || null,
    partner: {
      provider: 'thyrocare',
      orderId: partner.orderId,
      orderNo: partner.orderNo,
      status: partner.status,
      message: partner.message,
      refOrderNo,
      createOrderRequest: createOrderPayload,
      createOrderResponse: partner.raw,
    },
    payment: {
      provider: 'razorpay',
      paymentId: razorpay?.razorpay_payment_id || null,
      orderId: razorpay?.razorpay_order_id || null,
      signature: razorpay?.razorpay_signature || null,
    },
    createdAt: new Date().toISOString(),
  };
}

export function buildLabTestOrderApiPayload(stored, { collectionAddressId = null, navigatorId = null } = {}) {
  const parseTime = (t) => {
    if (!t) return null;
    const s = String(t);
    return s.length === 5 ? `${s}:00` : s;
  };
  const externalPatientRef =
    stored.partner?.createOrderRequest?.patients?.[0]?.attributes?.externalPatientId
    || String(stored.patientId || '')
    || null;

  return {
    patientId: asUuidOrNull(stored.patientId),
    navigatorId: asUuidOrNull(navigatorId),
    testCode: stored.testCode,
    status: stored.status || 'ORDERED',
    patientName: stored.patientName,
    patientCode: stored.patientCode,
    patientPhone: stored.patientPhone,
    patientEmail: stored.patientEmail,
    externalPatientRef: externalPatientRef || '',
    planId: stored.planId,
    planName: stored.plan || stored.planName,
    noOfTests: stored.noOfTests ?? stored.testsIncluded?.length ?? null,
    price: stored.price,
    currency: stored.currency || 'INR',
    fastingRequired: stored.fastingRequired,
    appointmentDate: stored.appointmentDate || null,
    appointmentTime: parseTime(stored.appointmentTime),
    appointmentEndTime: parseTime(stored.appointmentEndTime),
    appointmentTimeZone: stored.appointmentTimeZone || 'Asia/Kolkata',
    collectionAddressId: asUuidOrNull(collectionAddressId),
    partnerProvider: stored.partner?.provider || 'thyrocare',
    partnerOrderId: stored.partnerOrderId || stored.partner?.orderId,
    partnerOrderStatus: stored.partnerOrderStatus || stored.partner?.status || stored.status,
    partnerRefOrderNo: stored.partner?.refOrderNo || stored.testCode,
    razorpayPaymentId: stored.razorpayPaymentId || stored.payment?.paymentId,
    razorpayOrderId: stored.razorpayOrderId || stored.payment?.orderId,
    razorpaySignature: stored.razorpaySignature || stored.payment?.signature,
  };
}

// ── Date / price helpers ──────────────────────────────────────────────────────
export const generateTestCode = () => `LAB-${Date.now().toString().slice(-6)}`;

export const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

/**
 * Formats a patient's stored phone number as Thyrocare's create-order API
 * requires it: '+91-XXXXXXXXXX' (country code, dash, exactly 10 digits).
 *
 * Previously just `'+91-' + phone.replace(/^\+91/, '')` — that only strips a
 * bare leading "+91" with no separator, so any other stored shape (a leading
 * "+91-" already present, "91XXXXXXXXXX" with no plus, spaces, or a stray
 * "0" prefix) either passed through untouched or got double-prefixed (e.g.
 * "+91-" + "-9876543210" = "+91--9876543210"), and Thyrocare's exact-format
 * regex rejected it with a 400 the create-order call had no recovery from.
 *
 * Fix: strip every non-digit character first, then take the last 10 digits
 * — that's the actual subscriber number regardless of what country-code
 * prefix (91, 0091, +91, +91-, spaces, ...) the stored value carried.
 */
export const toThyrocareContactNumber = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const last10 = digits.length > 10 ? digits.slice(-10) : digits;
  return `+91-${last10}`;
};

export const defaultCatalogGenderFromPatient = (g) => {
  const u = String(g || '').toUpperCase();
  if (u === 'FEMALE' || u === 'F') return 'FEMALE';
  if (u === 'MALE'   || u === 'M') return 'MALE';
  if (u === 'OTHER'  || u === 'OTHERS' || u === 'NON-BINARY' || u === 'NON_BINARY') return 'OTHERS';
  return 'MALE';
};

export const formatPrice = (rate) => {
  if (!rate) return '—';
  const cur  = rate.currency === 'INR' ? '₹' : (rate.currency || '');
  const sell = rate.sellingPrice ?? rate.listingPrice;
  if (sell == null) return '—';
  return `${cur}${Number(sell).toLocaleString('en-IN')}`;
};

export const groupTests = (testsIncluded = []) => {
  const map = new Map();
  for (const t of testsIncluded) {
    const g = t.groupName || 'OTHER';
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(t);
  }
  return Array.from(map.entries()).map(([groupName, tests]) => ({ groupName, tests }));
};

// 27 Jul 2026 — new. Was missing entirely — the only "today" reference
// anywhere in this file was tomorrowISO(), which is why AddressSlotStep.jsx's
// date input used `min={tomorrowISO()}` and blocked navigators from booking
// a lab test for the current date at all. Deepu's explicit direction, 27 Jul
// 2026: allow today.
export const todayISO = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

export const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export const maxBookingDateISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

export const formatDisplayDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

// ── API error formatters ──────────────────────────────────────────────────────
export const formatLabTestApiErrorBody = (data) => {
  if (!data || typeof data !== 'object') return '';
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors
      .map((e) => (e && typeof e === 'object' ? (e.message || e.code || '') : String(e)).trim())
      .filter(Boolean)
      .join(' ');
  }
  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  return '';
};

export const formatSlotsSearchError = (err) => {
  const fromBody = formatLabTestApiErrorBody(err.response?.data);
  if (fromBody) return fromBody;
  return err?.message || 'Failed to fetch slots.';
};

// ── Address constants & mappers ───────────────────────────────────────────────
export const EMPTY_NEW_ADDR = {
  addressType: 'HOME',
  street: '',
  city: '',
  state: 'Karnataka',
  zipCode: '',
  country: 'India',
  landmark: '',
  isDefault: false,
};

export const INDIAN_STATES = [
  'Karnataka', 'Tamil Nadu', 'Maharashtra', 'Telangana', 'Kerala',
  'Andhra Pradesh', 'Delhi', 'Gujarat', 'Rajasthan', 'Other',
];

export const mapPatientRecordToLabAddress = (addr) => {
  if (!addr) {
    return {
      houseNo: '', street: '', addressLine1: '', addressLine2: '',
      landmark: '', city: '', state: 'Karnataka', country: 'India', pincode: '',
    };
  }
  return {
    houseNo: '',
    street:       addr.street || '',
    addressLine1: addr.addressLine1 || addr.street || '',
    addressLine2: addr.addressLine2 || addr.area   || '',
    landmark:     addr.landmark || '',
    city:         addr.city    || '',
    state:        addr.state   || 'Karnataka',
    country:      addr.country || 'India',
    pincode:      String(addr.zipCode ?? addr.pincode ?? ''),
  };
};

export const newAddrFormToLabAddress = (form) => ({
  houseNo: '',
  street:       form.street   || '',
  addressLine1: form.street   || '',
  addressLine2: '',
  landmark:     form.landmark || '',
  city:         form.city     || '',
  state:        form.state    || 'Karnataka',
  country:      form.country  || 'India',
  pincode:      String(form.zipCode || ''),
});

// ── Slot helpers ──────────────────────────────────────────────────────────────
export const extractSlots = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return (
    data.slotsList || data.slots || data.timeSlots ||
    data.availableSlots || data.data?.slots || data.data?.slotsList || []
  );
};

export const slotLabel = (s) => {
  if (!s) return '';
  const start = s.startTime || s.slotTime || s.time || s.from || '';
  const end   = s.endTime   || s.toTime   || s.to   || '';
  return end ? `${start} – ${end}` : start;
};

export const slotStart = (s) => s?.startTime || s?.slotTime || s?.time || s?.from || '';

export const slotIsAvailable = (s) =>
  s?.available !== false && s?.isAvailable !== false && s?.isBlocked !== true;

// ── Booking stepper config ────────────────────────────────────────────────────
export const BOOKING_STEPS = [
  { id: 1, label: 'Patient',   hint: 'Who is the test for?' },
  { id: 2, label: 'Test plan', hint: 'Choose a package' },
  { id: 3, label: 'Schedule',  hint: 'Address & time slot' },
  { id: 4, label: 'Pay',       hint: 'Confirm & pay' },
];

// ── Order detail helpers ──────────────────────────────────────────────────────
export const formatApiError = (data) => {
  if (!data || typeof data !== 'object') return '';
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors.map((e) => e?.message || e?.code || '').filter(Boolean).join(' ');
  }
  return typeof data.message === 'string' ? data.message : '';
};

export const formatMoney = (v, currency = 'INR') => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const sym = currency === 'INR' ? '₹' : `${currency} `;
  return `${sym}${n.toLocaleString('en-IN')}`;
};

export const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const statusBadgeClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('COMPLETED')) return 'badge-active';
  if (s.includes('CANCEL'))    return 'badge-inactive';
  if (s.includes('PLACED') || s.includes('ORDER')) return 'badge-pending';
  return 'badge-confirmed';
};

// ─────────────────────────────────────────────────────────────────────────────
// Address type label — DEF-039
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Human-readable label for an address type.
 *
 * DEF-039 — "Address badge shows DEFAU instead of DEFAULT — text truncated."
 *
 * Two things were wrong with how that chip was rendered, and this fixes the one
 * that is a code defect:
 *
 *   1. It printed the RAW enum token (`{addr.addressType}`) with
 *      textTransform: uppercase and letter-spacing on top, inside a card that
 *      also carries an absolutely-positioned "Default" badge over the same
 *      corner. A long token had nowhere to go and visibly clipped. Raw enum
 *      tokens reaching a user is the same class of leak as TC-D001 flagged for
 *      doctor action items.
 *
 *   2. The literal string "DEFAULT" is not a value this code can produce:
 *      Address.AddressType is HOME | OFFICE | OTHER, AddressService calls
 *      AddressType.valueOf() (so anything else is a 400), and
 *      V1__create_patient_management_tables.sql has
 *      CHECK (address_type IN ('HOME','OFFICE','OTHER')). Whatever the tester
 *      saw, no code path here writes or reads a DEFAULT address type — most
 *      likely the "Default" badge itself clipping, or environment-specific data.
 *
 * So this returns a proper label and, for a value it does not recognise,
 * Title-cases it rather than passing an unknown token through in caps. Unknown
 * values are surfaced as-is-but-readable instead of being mapped to "Other",
 * which would hide the very data problem worth seeing.
 */
export const addressTypeLabel = (addressType) => {
  const raw = String(addressType || '').trim();
  if (!raw) return 'Home';
  switch (raw.toUpperCase()) {
    case 'HOME':   return 'Home';
    case 'OFFICE': return 'Office';
    case 'WORK':   return 'Office';
    case 'OTHER':  return 'Other';
    default:
      return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }
};
